import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import useLocalStorageState from 'use-local-storage-state'

import { promotionsApi } from '../../lib'

import type { Category } from '../../types'

interface LocalStorageData {
  data: Category[]
  timestamp: number
}

const CATEGORY_ID = '402'
const PAGE_SIZE = '50'
const CURRENT_PAGE = '1'
const TIME_TO_REFRESH = 30 * 60 * 1000 // 30 minutes

const isValidData = (data: LocalStorageData | null): boolean => {
  if (!data?.timestamp) return false
  const now = new Date().getTime()
  const elapsed = now - data.timestamp
  return elapsed < TIME_TO_REFRESH
}

interface CategoriesDataResult {
  categoriesData: Category[]
  firstLevelCategories: string[]
  loading: boolean
  error: Error | null
}

/**
 * Hook for fetching and caching categories data
 */
export function useCategoriesData(): CategoriesDataResult {
  const [localData, setLocalData] = useLocalStorageState<LocalStorageData | null>('promo_data', {
    defaultValue: null,
  })

  const hasValidLocalData = localData && isValidData(localData)

  const query = useQuery<Category[]>({
    queryKey: ['categoriesData'],
    queryFn: async () => {
      const response = await promotionsApi.getCategoriesWithProducts(CATEGORY_ID, PAGE_SIZE, CURRENT_PAGE)
      return response.categoryList
    },
    initialData: hasValidLocalData ? localData.data : undefined,
    enabled: !hasValidLocalData,
    staleTime: TIME_TO_REFRESH,
  })

  // Update local storage when fresh data arrives
  // Using useEffect since onSuccess is deprecated in TanStack Query v5
  // and state mutations during render are not allowed in React 19
  useEffect(() => {
    if (query.data && !query.isFetching && query.isSuccess) {
      const newTimestamp = new Date().getTime()
      if (!localData || localData.timestamp < newTimestamp - 1000) {
        setLocalData({
          data: query.data,
          timestamp: newTimestamp,
        })
      }
    }
  }, [query.data, query.isFetching, query.isSuccess, localData, setLocalData])

  const categoriesData = query.data ?? localData?.data ?? []

  const firstLevelCategories = categoriesData.map((category) => category.name)

  return {
    categoriesData,
    firstLevelCategories,
    loading: query.isLoading,
    error: query.error,
  }
}
